<template>
    <div class="page__content translations" :class="{ 'sticky-header': headerSticky }">
        <h2 class="page__headline">{{ $t('translations.headline') }}</h2>
        <div class="columns">
            <div class="column is-8">
                <p class="page__copy">
                    {{ $t('translations.copy') }}
                </p>
            </div>
        </div>

        <div class="translations-container">
            <section class="header is-clearfix section" :class="{ 'sticky': headerSticky }">
                <div class="left">
                    <h3>{{ $t('translations.header.source') }}</h3>
                    <span class="select">
                        <select id="source_language" v-model="source_language" name="source_language">
                            <option v-for="(lang, _) in languages" :value="lang.code" :key="lang.code" v-if="lang.code !== target_language">
                                {{ lang.name }}
                            </option>
                        </select>
                    </span>
                </div>
                <div class="right">
                    <h3>{{ $t('translations.header.dest') }}</h3>
                    <span class="select">
                        <select id="target_language" v-model="target_language" name="target_language" @focus.stop.prevent="">
                            <option v-for="(lang, _) in languages" :value="lang.code" :key="lang.code" v-if="lang.code !== source_language">
                                {{ lang.name }}
                            </option>
                        </select>
                    </span>
                </div>
                <div class="bottom">
                    <label class="checkbox">
                        <input type="checkbox" v-model="showOnlyUntranslated">
                        {{ $t('translations.header.showOnlyUntranslated') }}
                    </label>
                </div>
            </section>
            <section v-for="(section, sectionIndex) of translations" class="section item-wrapper" :key="sectionIndex" v-if="shouldSectionBeShown(sectionIndex)">
                <div class="section-header">
                    <h3>{{ $t(`generic.menu.${sectionIndex}`) }}</h3>
                </div>
                <div class="items">
                    <div class="item is-clearfix box-shadow" v-for="(item, itemIndex) of section" :key="`${sectionIndex}.${itemIndex}`" v-if="shouldItemBeShown(sectionIndex, itemIndex)">
                        <div class="src">
                            <div v-if="!!item.code" class="code">Code: <strong>{{ item.code }}</strong></div>
                            <div v-for="(field, fieldIndex) of item.fields" class="field" v-if="!(sectionIndex === 'seasons' && field.attributes.name === 'description')" :key="`${sectionIndex}.${itemIndex}.${fieldIndex}`">
                                <div v-for="(trans, transIndex) of field.translations" :key="`${sectionIndex}.${itemIndex}.${fieldIndex}.${transIndex}`" v-if="shouldFieldBeShown(sectionIndex, itemIndex, fieldIndex)">
                                    <translation-field
                                        v-model="translations[sectionIndex][itemIndex].fields[fieldIndex].translations[transIndex]"
                                        :section="sectionIndex"
                                        :lang="source_language"
                                        :attributes="field.attributes"
                                        :readonly="true"
                                        @copy-content="copyContent(sectionIndex, itemIndex, fieldIndex, $event)" v-if="trans.lang === source_language">
                                    </translation-field>
                                </div>
                            </div>
                        </div>
                        <div class="dest">
                            <div v-if="!!item.code" class="code">Code: <strong>{{ item.code }}</strong></div>
                            <div v-for="(field, fieldIndex) of item.fields" class="field" v-if="!(sectionIndex === 'seasons' && field.attributes.name === 'description')" :key="`${sectionIndex}.${itemIndex}.${fieldIndex}`">
                                <div v-for="(trans, transIndex) of field.translations" :key="`${sectionIndex}.${itemIndex}.${fieldIndex}.${transIndex}`" v-if="shouldFieldBeShown(sectionIndex, itemIndex, fieldIndex)">
                                    <translation-field
                                        v-model="translations[sectionIndex][itemIndex].fields[fieldIndex].translations[transIndex]"
                                        :section="sectionIndex"
                                        :lang="target_language"
                                        :attributes="field.attributes"
                                        :readonly="false"
                                        :missing="isTranslationMissing(sectionIndex, itemIndex, fieldIndex)"
                                        v-if="trans.lang === target_language">
                                    </translation-field>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>

        <div class="edit-box__footer is-clearfix">
            <p class="control">
                <button class="button is-primary is-pulled-right" :class="{ 'is-loading' : loading }" @click.stop.prevent="save()">{{ $t('generic.save') }}</button>
            </p>
        </div>
    </div>
</template>

<script>
    import { cloneDeep } from 'lodash';

    import { AppModel } from '../../../models';
    import { Api } from '../../../api';
    import { TranslationField } from '../../components';
    import { EventBus } from '../../../utils';

    export default {
        name: 'translations',

        components: {
            TranslationField,
        },

        data() {
            return {
                translations: {},
                oldTranslations: {},
                target_language: 'de',
                source_language: 'de',
                detectLanguageChange: false,
                loading: true,
                languages: [],
                boundScrollHandler: null,
                headerElement: null,
                headerSticky: false,
                showOnlyUntranslated: false,
            };
        },

        beforeRouteEnter(to, from, next) {
            let baseLang = 'de';
            let lang = {};
            Api.bootstrap().then(function(response) {
                baseLang = AppModel.activeCampground.settings.base_language;
                for(let l of AppModel.languagesSorted) {
                    if(l.code !== baseLang) {
                        lang = l;
                        break;
                    }
                }
                return Promise.all([response, Api.translations.fetch({source_language: baseLang, target_language: lang.code})]);
            }).then(function(response) {
                next((vm) => {
                    vm.target_language = lang.code;
                    vm.source_language = baseLang;
                    vm.translations = cloneDeep(response[1].body);
                    vm.oldTranslations = cloneDeep(response[1].body);
                    vm.$nextTick(() => {
                        vm.detectLanguageChange = true;
                        vm.loading = false;
                    });
                });
            });
        },

        created() {
            this.languages = AppModel.languagesSorted;
            this.boundScrollHandler = this.onScroll.bind(this);
        },

        mounted() {
            window.addEventListener('scroll', this.boundScrollHandler);
            this.boundScrollHandler();
        },

        destroyed() {
            window.removeEventListener('scroll', this.boundScrollHandler);
        },

        watch: {
            target_language() {
                if(this.detectLanguageChange) {
                    this.refetch();
                }
            },
            source_language() {
                if(this.detectLanguageChange) {
                    this.refetch();
                }
            },
            translations: {
                handler: () => {},
                deep: true,
            },
        },

        methods: {
            onScroll() {
                if(this.$el && this.headerElement === null) {
                    this.headerElement = this.$el.getElementsByClassName('header')[0];
                    window.headerElement = this.headerElement;
                }

                let y = this.headerElement.offsetTop - window.scrollY;
                if(this.headerSticky) {
                    y += 50;
                }
                this.headerSticky = y <= 50;
            },
            refetch() {
                this.loading = true;
                Api.translations.fetch({source_language: this.source_language, target_language: this.target_language}).then((response) => {
                    this.translations = cloneDeep(response.body);
                    this.oldTranslations = cloneDeep(response.body);
                    this.loading = false;
                });
            },

            copyContent(sectionIndex, itemIndex, fieldIndex, val) {
                let sectionCopy = cloneDeep(this.translations[sectionIndex]);
                const translations = sectionCopy[itemIndex].fields[fieldIndex].translations;
                for(let [transIndex, trans] of Object.entries(translations)) {
                    if(trans.lang === this.target_language) {
                        sectionCopy[itemIndex].fields[fieldIndex].translations[transIndex].value = val;
                        this.$set(this.translations, sectionIndex, sectionCopy);
                        return;
                    }
                }
            },

            getTranslationDiff() {
                let diff = {};
                for(let [sectionIndex, section] of Object.entries(this.translations)) {
                    for(let obj of section) {
                        const objIndex = section.indexOf(obj);
                        for(let field of obj.fields) {
                            const fieldIndex = obj.fields.indexOf(field);
                            for(let trans of field.translations) {
                                const transIndex = field.translations.indexOf(trans);
                                if(trans.lang === this.target_language) {
                                    const oldVal = this.oldTranslations[sectionIndex][objIndex].fields[fieldIndex].translations[transIndex].value;
                                    let newVal = trans.value;
                                    let newValEmpty = !newVal;
                                    if(field.attributes.type === 'wysiwyg') {
                                        if(!newValEmpty && newVal !== null && newVal !== undefined) {
                                            newValEmpty = !newVal.replace(/(<([^>]+)>)/ig, '');
                                        }
                                    }
                                    if(!(oldVal === null && newValEmpty) && trans.value !== oldVal) {
                                        if(!diff[sectionIndex]) {
                                            diff[sectionIndex] = [];
                                        }
                                        let existingObjIndex = diff[sectionIndex].length;
                                        for(let existingObj of diff[sectionIndex]) {
                                            if(existingObj.id === obj.id) {
                                                existingObjIndex = diff[sectionIndex].indexOf(existingObj);
                                            }
                                        }
                                        if(!diff[sectionIndex][existingObjIndex]) {
                                            diff[sectionIndex][existingObjIndex] = {
                                                id: obj.id,
                                                fields: [],
                                            };
                                        }

                                        if(!diff[sectionIndex][existingObjIndex].fields) {
                                            diff[sectionIndex][existingObjIndex].fields = [];
                                        }

                                        let existingFieldIndex = diff[sectionIndex][existingObjIndex].fields.length;
                                        for(let existingField of diff[sectionIndex][existingObjIndex].fields) {
                                            if(existingField.attributes.name === field.attributes.name) {
                                                existingFieldIndex = diff[sectionIndex][existingObjIndex].fields.indexOf(existingField);
                                            }
                                        }
                                        diff[sectionIndex][existingObjIndex].fields[existingFieldIndex] = {
                                            attributes: {
                                                name: field.attributes.name,
                                            },
                                            translations: [{
                                                lang: trans.lang,
                                                value: trans.value,
                                            }],
                                        };
                                    }
                                }
                            }
                        }
                    }
                }
                return diff;
            },

            shouldSectionBeShown(sectionIndex) {
                if(!this.showOnlyUntranslated) {
                    return true;
                }
                for(let [itemIndex, item] of Object.entries(this.oldTranslations[sectionIndex])) {
                    for(let fld of Object.entries(item.fields)) {
                        if(this.shouldFieldBeShown(sectionIndex, itemIndex, fld[0])) {
                            return true;
                        }
                    }
                }
                return false;
            },

            shouldItemBeShown(sectionIndex, itemIndex) {
                if(!this.showOnlyUntranslated) {
                    return true;
                }
                for(let fld of Object.entries(this.oldTranslations[sectionIndex][itemIndex].fields)) {
                    if(this.shouldFieldBeShown(sectionIndex, itemIndex, fld[0])) {
                        return true;
                    }
                }
                return false;
            },

            shouldFieldBeShown(sectionIndex, itemIndex, fieldIndex) {
                if(!this.showOnlyUntranslated) {
                    return true;
                }
                let src = null;
                let dst = null;
                for(let trans of this.oldTranslations[sectionIndex][itemIndex].fields[fieldIndex].translations) {
                    if(trans.lang === this.source_language) {
                        src = trans.value;
                    } else if(trans.lang === this.target_language) {
                        dst = trans.value;
                    }
                }
                if(src === null || src === undefined || src.replace(/(<([^>]+)>)/ig, '') === '') {
                    return false;
                }
                return dst === null || dst === undefined || dst.replace(/(<([^>]+)>)/ig, '') === '';
            },

            isTranslationMissing(sectionIndex, itemIndex, fieldIndex) {
                let src = null;
                let dst = null;
                for(let trans of this.oldTranslations[sectionIndex][itemIndex].fields[fieldIndex].translations) {
                    if(trans.lang === this.source_language) {
                        src = trans.value;
                    } else if(trans.lang === this.target_language) {
                        dst = trans.value;
                    }
                }
                return src && src.replace(/(<([^>]+)>)/ig, '') !== '' && (dst === null || dst === undefined || dst.replace(/(<([^>]+)>)/ig, '') === '');
            },

            save() {
                const diff = this.getTranslationDiff();
                this.loading = true;
                Api.translations.save(this.target_language, diff).then(() => {
                    this.loading = false;
                    this.oldTranslations = cloneDeep(this.translations);

                    EventBus.$emit(EventBus.UPDATE_CHECKS);
                    EventBus.$emit(EventBus.SHOW_NOTIFICATION, { title: `<h3>${this.$t('generic.saveSuccessHeadline')}</h3>`, direction: 'Up', body: this.$t('generic.saveSuccess') });
                    setTimeout(() => {
                        window.scrollTo(0, 9999999);
                    }, 0);
                });
            },
        },
    };
</script>

<style lang="scss">
    @import './translations.scss';
</style>



// WEBPACK FOOTER //
// src/views/pages/translations/translations.vue